"use client";

import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Rating,
  Alert,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";
import ReviewWritingAssistant from "@/app/components/ai/ReviewWritingAssistant";
import { useToast } from "@/utils/theme/useToast";

const ReviewSubmissionModal = ({
  open,
  onClose,
  sharerName,
  sharerId,
  meetingId,
  onReviewSubmitted,
  meetingSummary = null,
}) => {
  const toast = useToast();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [review, setReview] = useState("");
  const [privateFeedback, setPrivateFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const ratingLabels = {
    1: "Poor",
    2: "Fair",
    3: "Good",
    4: "Very Good",
    5: "Excellent",
  };

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setRating(0);
      setHoverRating(0);
      setReview("");
      setPrivateFeedback("");
      setError("");
      onClose();
    }
  }, [isSubmitting, onClose]);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;

    if (rating === 0) {
      setError("Please select a rating");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await fetch("/api/reviews", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sharer_id: sharerId,
          meeting_id: meetingId,
          rating: rating,
          comment: review.trim(),
          private_feedback: privateFeedback.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to submit review");
      }

      if (onReviewSubmitted) {
        onReviewSubmitted();
      } else {
        handleClose();
      }

      toast.show(
        "Review Submitted",
        "Your review has been submitted successfully.",
      );
    } catch (err) {
      setError(err.message || "Failed to submit review. Please try again.");
      console.error("Review submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    rating,
    review,
    privateFeedback,
    sharerId,
    meetingId,
    onReviewSubmitted,
    isSubmitting,
    handleClose,
    toast,
  ]);

  const feedbackFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "transparent",
      borderRadius: "12px",
      fontSize: "0.875rem",
      lineHeight: 1.6,
    },
    "& .MuiInputBase-input::placeholder": {
      opacity: 1,
      color: "text.secondary",
    },
    "& .MuiFormHelperText-root": {
      mx: 0,
      mt: 0.75,
      textAlign: "right",
    },
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={handleClose}
      title="How was your meeting?"
      subtitle={`with ${sharerName}`}
      showIgnoreButton={false}
      showConfirmButton={false}
      showCloseIcon={true}
      isProcessing={isSubmitting}
      disableBackdropClick={isSubmitting}
      disableEscapeKeyDown={isSubmitting}
      disableSwipeToClose={isSubmitting}
      modalStyle="center"
      backdropStyle="frosted"
      customStyle={{
        width: "min(620px, calc(100vw - 48px))",
        borderRadius: "16px",
        maxHeight: "calc(100dvh - 48px)",
        overflowY: "auto",
        px: 0,
      }}
      contentSx={{ p: 4 }}
      titleSx={{
        fontSize: "1.625rem",
        lineHeight: 1.25,
        letterSpacing: "-0.5px",
      }}
      subtitleSx={{ mb: 3, fontSize: "0.875rem" }}
      bodySx={{ mb: 0 }}
      mobileContentSx={{
        maxHeight: "92dvh",
        "& > .MuiBox-root:last-child": {
          px: 3,
          pb: "max(24px, env(safe-area-inset-bottom))",
          overflowY: "auto",
        },
        "& #confirm-dialog-description": {
          mb: 0,
          flexShrink: 0,
        },
      }}
      body={
        <Box
          sx={{
            textAlign: "left",
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          {error && (
            <Alert severity="error" onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 1.5,
              }}
            >
              <Rating
                name="meeting-rating"
                disabled={isSubmitting}
                getLabelText={(value) =>
                  `${value} ${value === 1 ? "star" : "stars"}: ${
                    ratingLabels[value]
                  }`
                }
                value={rating}
                onChange={(event, newValue) => {
                  setRating(newValue || 0);
                  setError("");
                }}
                onChangeActive={(event, newHover) => {
                  setHoverRating(newHover);
                }}
                size="large"
                icon={<StarIcon sx={{ fontSize: "2.25rem" }} />}
                emptyIcon={<StarBorderIcon sx={{ fontSize: "2.25rem" }} />}
                sx={{
                  gap: 1,
                  "& .MuiRating-label": { p: 0.5 },
                  "& .MuiRating-iconEmpty": { color: "text.disabled" },
                  "& .MuiRating-iconFilled": {
                    color: "text.primary",
                  },
                  "& .MuiRating-iconHover": {
                    color: "text.primary",
                  },
                }}
              />
            </Box>
            <Typography
              variant="body2"
              aria-live="polite"
              sx={{
                color: rating > 0 ? "text.primary" : "text.secondary",
                fontWeight: rating > 0 ? 600 : 400,
                minHeight: 22,
              }}
            >
              {hoverRating > 0
                ? ratingLabels[hoverRating]
                : rating > 0
                ? ratingLabels[rating]
                : "Choose a star rating to continue."}
            </Typography>
          </Box>

          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                component="label"
                htmlFor="meeting-review"
                variant="body2"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Your review
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    fontWeight: 400,
                    color: "text.secondary",
                    fontSize: "0.75rem",
                  }}
                >
                  Optional
                </Box>
              </Typography>
              <ReviewWritingAssistant
                currentReview={review}
                onUseContent={(content) => setReview(content)}
                rating={rating}
                sharerName={sharerName}
                meetingSummary={meetingSummary}
              />
            </Box>
            <TextField
              id="meeting-review"
              fullWidth
              multiline
              rows={2}
              placeholder={
                rating === 0
                  ? "Please select a rating first"
                  : "What did you appreciate about this meeting?"
              }
              value={review}
              onChange={(e) => setReview(e.target.value)}
              disabled={isSubmitting || rating === 0}
              slotProps={{
                htmlInput: {
                  maxLength: 500,
                },
              }}
              helperText={`${review.length}/500`}
              sx={feedbackFieldSx}
            />
          </Box>

          <Box>
            <Typography
              component="label"
              htmlFor="meeting-private-feedback"
              variant="body2"
              sx={{
                display: "block",
                fontWeight: 600,
                color: "text.primary",
                mb: 0.5,
              }}
            >
              Private feedback
              <Box
                component="span"
                sx={{
                  ml: 1,
                  fontWeight: 400,
                  color: "text.secondary",
                  fontSize: "0.75rem",
                }}
              >
                Optional
              </Box>
            </Typography>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
            >
              <Typography variant="caption" color="text.secondary">
                Only visible to Meex admins
              </Typography>
            </Box>
            <TextField
              id="meeting-private-feedback"
              fullWidth
              multiline
              rows={2}
              placeholder={
                rating === 0
                  ? "Please select a rating first"
                  : "Any feedback you'd like to share privately?"
              }
              value={privateFeedback}
              onChange={(e) => setPrivateFeedback(e.target.value)}
              disabled={isSubmitting || rating === 0}
              slotProps={{
                htmlInput: {
                  maxLength: 500,
                },
              }}
              helperText={`${privateFeedback.length}/500`}
              sx={feedbackFieldSx}
            />
          </Box>
          <Button
            variant="text"
            disabled={rating === 0 || isSubmitting}
            onClick={handleSubmit}
            sx={{
              minHeight: 48,
              width: { xs: "100%", sm: "auto" },
              borderRadius: "12px",
              alignSelf: "flex-end",
              px: { xs: 3, sm: 0.5 },
              fontWeight: 600,
              bgcolor: { xs: "text.primary", sm: "transparent" },
              color: { xs: "background.paper", sm: "text.primary" },
              "&:hover": {
                bgcolor: { xs: "text.primary", sm: "transparent" },
                color: { xs: "background.paper", sm: "text.secondary" },
              },
              "&:disabled": {
                bgcolor: { xs: "action.disabledBackground", sm: "transparent" },
                color: "text.disabled",
              },
            }}
          >
            {isSubmitting ? "Submitting..." : "Submit review"}
          </Button>
        </Box>
      }
      minHeight="auto"
      mobileMinHeight="auto"
    />
  );
};

export default ReviewSubmissionModal;
